import { useState, useEffect, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import GridViewIcon from '@mui/icons-material/GridView';
import GridOnIcon from '@mui/icons-material/GridOn';
import Filter2Icon from '@mui/icons-material/Filter2';
import LensIcon from '@mui/icons-material/Lens';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import RadioIcon from '@mui/icons-material/Radio';
import { useFetch } from '../../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../../config';

const iconMap = {
    Add: AddIcon,
    Chat: ChatIcon,
    GridView: GridViewIcon,
    GridOn: GridOnIcon,
    Filter2: Filter2Icon,
    Lens: LensIcon,
    GraphicEq: GraphicEqIcon,
    Radio: RadioIcon,
};

export function useCatalog() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const fetchWithAuth = useFetch();

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(null);
            try {
                const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/applications`);
                if (!response.ok) throw new Error(await response.text());
                const apps = await response.json();
                if (cancelled) return;

                const grouped = {};
                for (const app of apps) {
                    if (!grouped[app.category]) {
                        grouped[app.category] = {
                            labelKey: `APPLICATIONS.CATEGORY_${app.category}`,
                            apps: [],
                        };
                    }
                    grouped[app.category].apps.push({
                        id: app.id,
                        titleKey: `APPLICATIONS.${app.machine_name}.TITLE`,
                        descKey: `APPLICATIONS.${app.machine_name}.DESC`,
                        icon: iconMap[app.icon] || GridViewIcon,
                        route: app.route,
                        ai: app.use_ai,
                    });
                }

                setCategories(Object.values(grouped));
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [fetchWithAuth]);

    const allAppRoutes = useMemo(
        () => categories.flatMap(c => c.apps).map(a => a.route).filter(Boolean),
        [categories]
    );

    return { categories, allAppRoutes, loading, error };
}
